import PageHero from "@/components/PageHero";
import CTA from "@/components/CTA";
import { motion } from "framer-motion";

const VALUES = [
  { title: "Clarity over noise", body: "We cut the ceremony. Every screen earns its place." },
  { title: "Craft in the details", body: "The small things aren't small. We sweat the details other software skips." },
  { title: "Real workflows first", body: "We design against real dental operations, not slide decks." },
  { title: "Security as a default", body: "Encryption, role-based access, and audit logs come standard, not as an upsell." },
];

const TIMELINE = [
  { year: "2023", title: "Rxn3D founded", body: "Started as a private tool for a small dental lab tired of paper Rx." },
  { year: "2024", title: "Public beta", body: "Rolled out to dozens of labs and dental offices. Digital Rx became the default." },
  { year: "2025", title: "Production and Drivers", body: "Launched production timelines, driver routes, and proof of delivery." },
  { year: "2026", title: "Multi-location access", body: "Added the ability to work across multiple locations, and even multiple labs, from a single account." },
];

export default function About() {
  return (
    <>
      <PageHero
        chapter="§ 09"
        eyebrow="About"
        title={"We build software for the people who run dental."}
        description="Rxn3D is built by a team who spent years inside dental labs and clinics, watching great teams get held back by paper and phone calls."
      />

      <section data-testid="about-mission" className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 rounded-3xl hairline glass p-8 lg:p-12"
          >
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-4">Mission</div>
            <h3 className="font-heading text-3xl sm:text-4xl font-semibold tracking-[-0.02em]">
              Mission: To make every dental case digital, visible, and closable, from start to finish.
            </h3>
            <p className="mt-6 text-base text-muted-foreground leading-relaxed">
              Dental labs and dental offices are the hidden engine of modern dentistry. They deserve tools as sharp as the work they do.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 rounded-3xl bg-brand-ink text-white p-8 lg:p-12 relative overflow-hidden grain"
          >
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-brand-gradient blur-3xl opacity-70" />
            <div className="relative">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 mb-4">Vision</div>
              <h3 className="font-heading text-3xl sm:text-4xl font-semibold tracking-[-0.02em]">
                Vision: The operating system for modern dental operations.
              </h3>
              <p className="mt-6 text-sm text-white/70 leading-relaxed">
                One platform. Every seat. Every case. Every drop-off.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="about-values" className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">Values: The way we work.</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-[-0.03em]">The way we work.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="rounded-3xl hairline glass p-6"
                data-testid={`value-${i}`}
              >
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  V·{i + 1}
                </div>
                <div className="mt-4 font-heading text-lg font-semibold">{v.title}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.body}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-timeline" className="pb-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">Timeline: A short history.</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-[-0.03em]">A short history.</h2>

          <div className="mt-10 relative">
            <div className="absolute left-3 top-4 bottom-4 w-px bg-gradient-to-b from-brand-cyan via-brand-plum to-brand-rose opacity-50" />
            <div className="space-y-8">
              {TIMELINE.map((t, i) => (
                <motion.div
                  key={t.year}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.06 }}
                  className="pl-14 relative"
                  data-testid={`about-year-${t.year}`}
                >
                  <div className="absolute left-0 top-1 h-6 w-6 rounded-full bg-brand-gradient shadow-[0_0_0_4px_var(--tw-background)] dark:shadow-[0_0_0_4px_hsl(224_71%_4%)]" />
                  <div className="font-mono text-xs text-muted-foreground">{t.year}</div>
                  <div className="font-heading text-2xl font-semibold mt-1">{t.title}</div>
                  <div className="mt-2 text-sm text-muted-foreground max-w-xl">{t.body}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTA />
    </>
  );
}
